'use client';
import React, { useState, useEffect } from 'react';
import { Plus, X, Loader2, ChevronDown } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import AuthGateModal from '@/components/AuthGateModal';
import { toast } from 'sonner';

interface CafeOption {
  id: string;
  name: string;
  area: string;
}

export default function FabUpload() {
  const { user } = useAuth();
  const supabase = createClient();
  const [authOpen, setAuthOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [cafes, setCafes] = useState<CafeOption[]>([]);
  const [selectedCafe, setSelectedCafe] = useState<CafeOption | null>(null);
  const [rating, setRating] = useState(5);
  const [content, setContent] = useState('');
  const [visitType, setVisitType] = useState('Santai');
  const [photos, setPhotos] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [cafeSearch, setCafeSearch] = useState('');
  const [showCafeDropdown, setShowCafeDropdown] = useState(false);

  useEffect(() => {
    if (!modalOpen) return;
    const fetchCafes = async () => {
      const { data } = await supabase
        .from('coffee_shops')
        .select('id, name, area')
        .order('name', { ascending: true });
      setCafes(data || []);
    };
    fetchCafes();
  }, [modalOpen]);

  const filteredCafes = cafes.filter(
    (c) =>
      c.name.toLowerCase().includes(cafeSearch.toLowerCase()) ||
      c.area.toLowerCase().includes(cafeSearch.toLowerCase())
  );

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setPhotos((prev) => [...prev, ...files].slice(0, 4));
  };

  const handleSubmit = async () => {
    if (!user) return;
    if (!selectedCafe) { toast.error('Pilih coffeeshop dulu!'); return; }
    if (!content.trim()) { toast.error('Tulis review kamu dulu!'); return; }

    setSubmitting(true);

    // Upload photos
    const photoUrls: string[] = [];
    for (const photo of photos) {
      const ext = photo.name.split('.').pop();
      const path = `${user.id}/${Date.now()}.${ext}`;
      const { error: uploadErr } = await supabase.storage.from('review-photos').upload(path, photo);
      if (!uploadErr) {
        const { data: { publicUrl } } = supabase.storage.from('review-photos').getPublicUrl(path);
        photoUrls.push(publicUrl);
      }
    }

    const { error } = await supabase.from('reviews').insert({
      coffee_shop_id: selectedCafe.id,
      user_id: user.id,
      rating,
      content: content.trim(),
      visit_type: visitType,
      photo_urls: photoUrls,
    });

    if (error) {
      toast.error('Gagal upload review: ' + error.message);
    } else {
      // Update review count on coffee shop
      await supabase.rpc('increment_review_count' as any, { shop_id: selectedCafe.id }).catch(() => {});
      toast.success('Review berhasil diupload! ✨');
      setModalOpen(false);
      setContent('');
      setPhotos([]);
      setSelectedCafe(null);
      setRating(5);
    }
    setSubmitting(false);
  };

  const handleFabClick = () => {
    if (!user) {
      setAuthOpen(true);
    } else {
      setModalOpen(true);
    }
  };

  return (
    <>
      <button
        onClick={handleFabClick}
        className="fixed bottom-24 right-5 lg:bottom-8 lg:right-8 w-14 h-14 bg-accent rounded-2xl shadow-fab flex items-center justify-center btn-press z-30 hover:bg-[#E06828] transition-colors"
        aria-label="Upload review atau foto cafe"
      >
        <Plus size={26} color="white" strokeWidth={2.5} />
      </button>

      <AuthGateModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        action="upload foto dan review tempat"
      />

      {/* Upload Review Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setModalOpen(false)}>
          <div className="bg-background rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h2 className="text-base font-800 text-primary">✍️ Upload Review</h2>
              <button onClick={() => setModalOpen(false)} className="p-2 hover:bg-secondary rounded-xl transition-colors">
                <X size={18} className="text-muted-foreground" />
              </button>
            </div>

            <div className="p-5 flex flex-col gap-4">
              {/* Cafe Selector */}
              <div>
                <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wider">Pilih Coffeeshop</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Cari nama cafe..."
                    value={selectedCafe ? selectedCafe.name : cafeSearch}
                    onChange={(e) => { setCafeSearch(e.target.value); setSelectedCafe(null); setShowCafeDropdown(true); }}
                    onFocus={() => setShowCafeDropdown(true)}
                    className="input-field pr-8"
                  />
                  <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  {showCafeDropdown && filteredCafes.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-lg z-10 max-h-48 overflow-y-auto">
                      {filteredCafes.slice(0, 20).map((c) => (
                        <button
                          key={c.id}
                          onClick={() => { setSelectedCafe(c); setCafeSearch(''); setShowCafeDropdown(false); }}
                          className="w-full text-left px-4 py-2.5 hover:bg-secondary transition-colors text-sm"
                        >
                          <span className="font-600 text-foreground">{c.name}</span>
                          <span className="text-muted-foreground ml-2 text-xs">{c.area}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {selectedCafe && (
                  <p className="text-xs text-accent font-600 mt-1">✓ {selectedCafe.name} — {selectedCafe.area}</p>
                )}
              </div>

              {/* Rating */}
              <div>
                <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wider">Rating</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <button key={s} onClick={() => setRating(s)} className={`text-2xl transition-transform hover:scale-110 ${s <= rating ? '' : 'opacity-30'}`}>
                      ⭐
                    </button>
                  ))}
                  <span className="text-sm font-700 text-foreground ml-2 self-center">{rating}/5</span>
                </div>
              </div>

              {/* Visit Type */}
              <div>
                <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wider">Tipe Kunjungan</label>
                <div className="flex gap-2 flex-wrap">
                  {['Santai', 'WFH', 'Date', 'Nongkrong', 'Solo'].map((type) => (
                    <button
                      key={type}
                      onClick={() => setVisitType(type)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-600 border transition-colors ${visitType === type ? 'bg-accent text-white border-accent' : 'bg-card border-border text-muted-foreground hover:border-accent'}`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {/* Review Text */}
              <div>
                <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wider">Review</label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Ceritain pengalaman nongkrong kamu di sini..."
                  rows={4}
                  className="input-field resize-none text-sm"
                />
              </div>

              {/* Photo Upload */}
              <div>
                <label className="block text-xs font-600 text-muted-foreground mb-1.5 uppercase tracking-wider">Foto (maks. 4)</label>
                <div className="flex gap-2 flex-wrap">
                  {photos.map((photo, i) => (
                    <div key={i} className="relative w-16 h-16 rounded-xl overflow-hidden border border-border">
                      <img src={URL.createObjectURL(photo)} alt={`Preview ${i + 1}`} className="w-full h-full object-cover" />
                      <button
                        onClick={() => setPhotos((prev) => prev.filter((_, idx) => idx !== i))}
                        className="absolute top-0.5 right-0.5 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center"
                      >
                        <X size={10} color="white" />
                      </button>
                    </div>
                  ))}
                  {photos.length < 4 && (
                    <label className="w-16 h-16 rounded-xl border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:border-accent transition-colors">
                      <Plus size={20} className="text-muted-foreground" />
                      <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotoChange} />
                    </label>
                  )}
                </div>
              </div>

              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="btn-primary w-full h-12 text-sm mt-1"
              >
                {submitting ? <Loader2 size={18} className="animate-spin" /> : 'Upload Review 🚀'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}